// features/workstations/dashboard-sections/engine-action-bar.js
// ═══════════════════════════════════════════════════════════
// ENGINE ACTION BAR — thin interactive footer for briefing rows
// ═══════════════════════════════════════════════════════════
// Wraps a row (option / tension) with buttons that POST to the
// appropriate engine endpoint:
//
//   kind='tension' → /api/engines/tensions/[id]
//     actions: acknowledge | resolve | dismiss
//
//   kind='option'  → /api/engines/options/[id]
//     actions: consider | acted_on | dismiss
//
// UI-only: optimistic state update on click; API call is
// fire-and-forget with error surface if it fails.
//
// Used INSIDE the workstation briefing widget only — not a
// shared primitive (those stay UI-pure and don't know about
// engines or APIs).
// ═══════════════════════════════════════════════════════════

import { useState } from 'react';

const CONFIG = {
  tension: {
    endpoint: (id) => `/api/engines/tensions/${id}`,
    actions: [
      { key: 'acknowledge', label: 'ack',     toneClass: 'ws-ea-btn-steel' },
      { key: 'resolve',     label: 'resolve', toneClass: 'ws-ea-btn-sage' },
      { key: 'dismiss',     label: 'dismiss', toneClass: 'ws-ea-btn-terra' },
    ],
  },
  option: {
    endpoint: (id) => `/api/engines/options/${id}`,
    actions: [
      { key: 'consider', label: 'consider', toneClass: 'ws-ea-btn-steel' },
      { key: 'acted_on', label: 'acted',    toneClass: 'ws-ea-btn-sage' },
      { key: 'dismiss',  label: 'dismiss',  toneClass: 'ws-ea-btn-terra' },
    ],
  },
  possibility: {
    endpoint: (id) => `/api/engines/possibilities/${id}`,
    actions: [
      { key: 'watch',      label: 'watching',    toneClass: 'ws-ea-btn-steel' },
      { key: 'confirm',    label: 'confirmed',   toneClass: 'ws-ea-btn-sage' },
      { key: 'invalidate', label: 'invalidated', toneClass: 'ws-ea-btn-terra' },
      { key: 'dismiss',    label: 'dismiss',     toneClass: 'ws-ea-btn-terra' },
    ],
  },
};

export default function EngineActionBar({ kind, id, userId, onTransition }) {
  const [state, setState] = useState('idle'); // 'idle' | 'working' | 'done' | 'error'
  const [resultLabel, setResultLabel] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const cfg = CONFIG[kind];

  if (!cfg || !id || !userId) return null;

  const act = async (action) => {
    if (state === 'working' || state === 'done') return;
    setState('working');
    setErrorMsg(null);
    try {
      const res = await fetch(cfg.endpoint(id), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, action }),
      });
      const data = res.ok ? await res.json() : null;
      if (!res.ok) {
        setState('error');
        setErrorMsg(data?.error || `HTTP ${res.status}`);
        return;
      }
      setState('done');
      setResultLabel(action);
      if (typeof onTransition === 'function') onTransition(action, data);
    } catch (e) {
      setState('error');
      setErrorMsg(e?.message || 'action failed');
    }
  };

  if (state === 'done') {
    return (
      <div className="ws-ea-bar ws-ea-bar-done">
        <span className="ws-ea-done-label">✓ {resultLabel}</span>
      </div>
    );
  }

  return (
    <div className="ws-ea-bar">
      {cfg.actions.map((a) => (
        <button
          key={a.key}
          type="button"
          className={`ws-ea-btn ${a.toneClass}`}
          onClick={() => act(a.key)}
          disabled={state === 'working'}
          title={a.label}
        >
          {state === 'working' ? '…' : a.label}
        </button>
      ))}
      {state === 'error' && (
        <span className="ws-ea-error" title={errorMsg || 'Action failed'}>
          ! {errorMsg ? errorMsg.slice(0, 40) : 'failed'}
        </span>
      )}
    </div>
  );
}
